import {
  ASSET_STATUS_LABELS,
  ASSET_STATUSES,
  ASSET_TYPES,
  assetTypeName,
  subject,
  type AssetDetail,
  type AssetListItem,
  type AssetStatus,
  type Paginated,
} from '@ftthnms/shared';
import { Badge, Button, EmptyState, RegionChip, StatusIndicator } from '@ftthnms/ui';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import {
  ChevronLeft,
  ChevronRight,
  Crosshair,
  MapPin,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { AssetDrawer, STATUS_TONE } from '../components/AssetDrawer';
import { AssetForm } from '../components/AssetForm';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { DeleteAssetDialog } from '../components/DeleteAssetDialog';
import { ExportMenu } from '../components/ExportMenu';
import { useToast } from '../components/Toast';
import { AssetGlyph } from '../map/AssetGlyph';
import { api, dateTime, nf, qs, send } from '../lib/api';
import { invalidateAssets } from '../lib/assets';
import { toIndicator } from '../lib/monitoring';
import { useSession } from '../lib/session';

type Panel = { kind: 'view'; id: string } | { kind: 'edit'; asset: AssetDetail } | { kind: 'create' };

const col = createColumnHelper<AssetListItem>();

export function AssetsPage() {
  const { area, ability } = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [trash, setTrash] = useState(false);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [panel, setPanel] = useState<Panel | null>(null);
  const [deleting, setDeleting] = useState<AssetListItem | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForce, setBulkForce] = useState(false);
  const pageSize = 25;

  const canDeleteAny = ability.can('delete', 'Asset');
  const canCreate = ability.can('create', 'Asset');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [debouncedQ, type, status, area, trash]);

  const list = useQuery({
    queryKey: ['assets', { area, q: debouncedQ, type, status, trash, page }],
    queryFn: () =>
      api<Paginated<AssetListItem>>(
        `/api/v1/assets${qs({ area, q: debouncedQ, type, status, trash, page, pageSize })}`,
      ),
    placeholderData: keepPreviousData,
  });
  const data = list.data;
  const rows = data?.items ?? [];
  const selectable = canDeleteAny && !trash;
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.id));

  async function restore(r: AssetListItem) {
    try {
      await send('POST', `/api/v1/assets/${r.id}/restore`);
      await invalidateAssets(qc);
      toast({ tone: 'success', text: `${r.code} dipulihkan.` });
    } catch (err) {
      toast({ tone: 'danger', text: (err as Error).message });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- mixed accessor/display columns
  const columns = useMemo<ColumnDef<AssetListItem, any>[]>(
    () => [
      ...(selectable
        ? [
            col.display({
              id: 'select',
              header: () => (
                <input
                  type="checkbox"
                  aria-label="Pilih semua di halaman ini"
                  checked={allOnPage}
                  onChange={(e) =>
                    setSelected((s) => {
                      const n = new Set(s);
                      for (const r of rows) {
                        if (e.target.checked) n.add(r.id);
                        else n.delete(r.id);
                      }
                      return n;
                    })
                  }
                />
              ),
              cell: ({ row }) => (
                <input
                  type="checkbox"
                  aria-label={`Pilih ${row.original.code}`}
                  checked={selected.has(row.original.id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) =>
                    setSelected((s) => {
                      const n = new Set(s);
                      if (e.target.checked) n.add(row.original.id);
                      else n.delete(row.original.id);
                      return n;
                    })
                  }
                />
              ),
            }),
          ]
        : []),
      col.display({
        id: 'asset',
        header: 'Aset',
        cell: ({ row }) => (
          <div className="fn-asset">
            <AssetGlyph type={row.original.typeCode} />
            <div>
              <div className="fn-asset__name">{row.original.customerName ?? row.original.name}</div>
              <div className="fn-asset__code">
                {row.original.code}
                {row.original.customerName && row.original.customerName !== row.original.name
                  ? ` · ${row.original.name}`
                  : ''}
              </div>
            </div>
          </div>
        ),
      }),
      col.accessor('typeCode', { header: 'Jenis', cell: (c) => assetTypeName(c.getValue()) }),
      col.accessor('areaCode', {
        header: 'Wilayah',
        cell: (c) => (c.getValue() ? <RegionChip code={c.getValue()!} /> : '—'),
      }),
      col.accessor('status', {
        header: 'Status',
        cell: (c) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
            <Badge tone={STATUS_TONE[c.getValue()] ?? 'neutral'} dot>
              {ASSET_STATUS_LABELS[c.getValue() as AssetStatus] ?? c.getValue()}
            </Badge>
            {c.row.original.monitor && <StatusIndicator status={toIndicator(c.row.original.monitor)} />}
          </div>
        ),
      }),
      trash
        ? col.accessor('deletedAt', {
            header: 'Dihapus',
            cell: (c) => (
              <span style={{ whiteSpace: 'nowrap' }}>
                {c.getValue() ? dateTime.format(new Date(c.getValue()!)) : '—'}
              </span>
            ),
          })
        : col.display({
            id: 'coord',
            header: 'Koordinat',
            cell: ({ row }) => (
              <span className="mono" style={{ whiteSpace: 'nowrap' }}>
                {row.original.lat.toFixed(6)}, {row.original.lng.toFixed(6)}
              </span>
            ),
          }),
      col.display({
        id: 'actions',
        header: () => <span style={{ display: 'block', textAlign: 'right' }}>Aksi</span>,
        cell: ({ row }) => {
          const r = row.original;
          const target = subject('Asset', { areaCode: r.areaCode });
          const stop = (fn: () => void) => (e: MouseEvent) => {
            e.stopPropagation();
            fn();
          };
          return (
            <div className="fn-table__actions" style={{ display: 'flex' }}>
              {trash ? (
                ability.can('delete', target) && (
                  <Button
                    size="sm"
                    icon={<RotateCcw className="i" aria-hidden />}
                    onClick={stop(() => void restore(r))}
                  >
                    Pulihkan
                  </Button>
                )
              ) : (
                <>
                  <button
                    type="button"
                    className="fn-icon-btn fn-icon-btn--sm"
                    aria-label={`Fokus ${r.code} di peta`}
                    title="Fokus di peta"
                    onClick={stop(() => void navigate({ to: '/map', search: { focus: r.id } }))}
                  >
                    <Crosshair className="i i--sm" aria-hidden />
                  </button>
                  {(ability.can('update', target) || ability.can('field_update', target)) && (
                    <button
                      type="button"
                      className="fn-icon-btn fn-icon-btn--sm"
                      aria-label={`Edit ${r.code}`}
                      title="Edit"
                      onClick={stop(
                        () =>
                          void api<AssetDetail>(`/api/v1/assets/${r.id}`).then((asset) =>
                            setPanel({ kind: 'edit', asset }),
                          ),
                      )}
                    >
                      <Pencil className="i i--sm" aria-hidden />
                    </button>
                  )}
                  {ability.can('delete', target) && (
                    <button
                      type="button"
                      className="fn-icon-btn fn-icon-btn--sm fn-icon-btn--danger"
                      aria-label={`Hapus ${r.code}`}
                      title="Hapus"
                      onClick={stop(() => setDeleting(r))}
                    >
                      <Trash2 className="i i--sm" aria-hidden />
                    </button>
                  )}
                </>
              )}
            </div>
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rebuilt when selection, rows or mode change
    [selectable, allOnPage, rows, selected, trash, ability],
  );

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount: data?.total ?? 0,
  });
  const lastPage = Math.max(1, Math.ceil((data?.total ?? 0) / pageSize));
  const from = data && data.total > 0 ? (page - 1) * pageSize + 1 : 0;
  const to = data ? Math.min(page * pageSize, data.total) : 0;
  const filtered = !!(debouncedQ || type || status);

  return (
    <div className="fn-page-body">
      <section className="fn-card" style={{ overflow: 'hidden' }}>
        <div className="fn-toolbar">
          <div className="fn-input-group" style={{ flex: 1, maxWidth: 320, minWidth: 180 }}>
            <Search className="i" aria-hidden />
            <input
              id="asset-search"
              className="fn-input"
              aria-label="Cari aset"
              placeholder="Cari nama, kode, pelanggan, SN…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <select
            id="asset-type"
            className="fn-input"
            aria-label="Jenis aset"
            style={{ width: 'auto' }}
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            <option value="">Semua jenis</option>
            {ASSET_TYPES.map((t) => (
              <option key={t.code} value={t.code}>
                {t.name}
              </option>
            ))}
          </select>
          <select
            id="asset-status"
            className="fn-input"
            aria-label="Status aset"
            style={{ width: 'auto' }}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">Semua status</option>
            {ASSET_STATUSES.map((s) => (
              <option key={s} value={s}>
                {ASSET_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
          {canDeleteAny && (
            <div className="fn-segmented" role="group" aria-label="Tampilan">
              <button
                type="button"
                className="fn-segmented__item"
                aria-pressed={!trash}
                onClick={() => setTrash(false)}
              >
                Aktif
              </button>
              <button
                type="button"
                className="fn-segmented__item"
                aria-pressed={trash}
                onClick={() => setTrash(true)}
              >
                <Trash2 className="i i--sm" aria-hidden /> Tempat sampah
              </button>
            </div>
          )}
          <span className="fn-toolbar__spacer" />
          {list.isFetching && <span className="fn-spinner" aria-label="Memuat" />}
          {!trash && ability.can('export', 'Asset') && (
            <ExportMenu entity="assets" params={{ area, q: debouncedQ, type, status }} kmz />
          )}
          {canCreate && !trash && (
            <Button
              variant="primary"
              size="sm"
              icon={<Plus className="i" aria-hidden />}
              onClick={() => setPanel({ kind: 'create' })}
            >
              Tambah Aset
            </Button>
          )}
        </div>

        {selectable && selected.size > 0 && (
          <div className="fn-bulkbar">
            <span>{nf.format(selected.size)} aset dipilih</span>
            <span className="fn-toolbar__spacer" />
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              Batal pilih
            </Button>
            <Button
              size="sm"
              variant="danger"
              icon={<Trash2 className="i" aria-hidden />}
              onClick={() => setBulkOpen(true)}
            >
              Hapus
            </Button>
          </div>
        )}

        {list.error ? (
          <div style={{ padding: 20 }}>
            <div className="fn-alert fn-alert--danger" role="alert">
              <div className="fn-alert__body">{list.error.message}</div>
            </div>
          </div>
        ) : data && data.total === 0 ? (
          <div style={{ padding: 20 }}>
            <EmptyState
              icon={
                trash ? (
                  <Trash2 className="i i--xl" aria-hidden />
                ) : (
                  <MapPin className="i i--xl" aria-hidden />
                )
              }
              title={
                trash ? 'Tempat sampah kosong' : filtered ? 'Tidak ada aset yang cocok' : 'Belum ada aset'
              }
              text={
                trash
                  ? 'Aset yang dihapus bisa dipulihkan di sini selama 30 hari.'
                  : filtered
                    ? 'Hapus filter atau cari dengan kata lain.'
                    : 'Tambahkan aset dari peta atau tombol Tambah Aset.'
              }
            />
          </div>
        ) : (
          <div className="fn-table-wrap">
            <table className="fn-table">
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((h) => (
                      <th key={h.id} className={h.id === 'select' ? 'is-check' : undefined}>
                        {flexRender(h.column.columnDef.header, h.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((r) => (
                  <tr
                    key={r.id}
                    aria-selected={
                      selected.has(r.original.id) || (panel?.kind === 'view' && panel.id === r.original.id)
                    }
                    onClick={() => setPanel({ kind: 'view', id: r.original.id })}
                    style={{ cursor: 'pointer' }}
                  >
                    {r.getVisibleCells().map((c) => (
                      <td key={c.id} className={c.column.id === 'select' ? 'is-check' : undefined}>
                        {flexRender(c.column.columnDef.cell, c.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="fn-table-footer">
          <div>
            Menampilkan{' '}
            <strong>
              {nf.format(from)}–{nf.format(to)}
            </strong>{' '}
            dari <strong>{nf.format(data?.total ?? 0)}</strong> aset
          </div>
          <nav className="fn-pagination" aria-label="Halaman">
            <button
              type="button"
              className="fn-page"
              disabled={page <= 1}
              aria-label="Sebelumnya"
              onClick={() => setPage((p) => p - 1)}
            >
              <ChevronLeft className="i i--sm" aria-hidden />
            </button>
            <span className="fn-page" aria-current="page">
              {page}
            </span>
            <span className="fn-hint">dari {lastPage}</span>
            <button
              type="button"
              className="fn-page"
              disabled={page >= lastPage}
              aria-label="Selanjutnya"
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight className="i i--sm" aria-hidden />
            </button>
          </nav>
        </div>
      </section>

      {panel?.kind === 'view' && (
        <AssetDrawer
          key={panel.id}
          id={panel.id}
          onClose={() => setPanel(null)}
          onEdit={(asset) => setPanel({ kind: 'edit', asset })}
          onOpenAsset={(id) => setPanel({ kind: 'view', id })}
          onFocus={(asset) => void navigate({ to: '/map', search: { focus: asset.id } })}
          onDeleted={() => setPanel(null)}
        />
      )}
      {panel?.kind === 'edit' && (
        <AssetForm
          key={`edit-${panel.asset.id}`}
          mode="edit"
          asset={panel.asset}
          onClose={() => setPanel({ kind: 'view', id: panel.asset.id })}
          onSaved={(a) => setPanel({ kind: 'view', id: a.id })}
        />
      )}
      {panel?.kind === 'create' && (
        <AssetForm
          mode="create"
          onClose={() => setPanel(null)}
          onSaved={(a) => {
            toast({ tone: 'success', text: `${a.code} ditambahkan.` });
            setPanel({ kind: 'view', id: a.id });
          }}
        />
      )}
      {deleting && (
        <DeleteAssetDialog
          asset={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => setDeleting(null)}
        />
      )}
      {bulkOpen && (
        <ConfirmDialog
          title={`Hapus ${nf.format(selected.size)} aset?`}
          icon={<Trash2 className="i" aria-hidden />}
          confirmLabel="Hapus"
          onClose={() => {
            setBulkOpen(false);
            setBulkForce(false);
          }}
          onConfirm={async () => {
            const res = await send<{ deleted: number; skipped: Array<{ id: string; reason: string }> }>(
              'POST',
              '/api/v1/assets/bulk-delete',
              { ids: [...selected], force: bulkForce },
            );
            await invalidateAssets(qc);
            setSelected(new Set(res.skipped.map((s) => s.id)));
            setBulkOpen(false);
            setBulkForce(false);
            toast({
              tone: res.skipped.length ? 'warning' : 'success',
              text: `${nf.format(res.deleted)} aset dipindah ke tempat sampah${res.skipped.length ? `; ${res.skipped.length} dilewati (${res.skipped[0]!.reason})` : ''}.`,
            });
          }}
        >
          <span>Aset dipindah ke tempat sampah dan bisa dipulihkan dalam 30 hari.</span>
          <label className="fn-choice" style={{ cursor: 'pointer' }}>
            <span>
              <span className="fn-choice__title">Hapus juga koneksi port</span>
              <span className="fn-choice__desc">
                Tanpa ini, aset yang masih terhubung ke port lain dilewati.
              </span>
            </span>
            <input
              type="checkbox"
              checked={bulkForce}
              onChange={(e) => setBulkForce(e.target.checked)}
              style={{ width: 18, height: 18, flex: 'none' }}
            />
          </label>
        </ConfirmDialog>
      )}
    </div>
  );
}
